import type { CCPreset } from '~/types/presets';
import type { MidiCC, DeviceProfile } from '~/types/midi';

/**
 * "Init" CC preset for YO! FM Synth
 *
 * This preset uses warm, rounded settings optimized for playing alongside MonoSynth
 * without causing phase interference or harsh buzzing.
 *
 * Key settings (optimized for multi-synth playback):
 * - harmonicity: 2.0 (octave - even harmonics avoid clashing with triangle's odd harmonics)
 * - modulationIndex: 3.0 (gentle FM depth - cleaner harmonic content)
 * - detune: 0 cents (no detuning - avoids slow beat frequencies that cause buzzing)
 * - carrier envelope: attack 0.03s, decay 0.2s, sustain 0.86, release 1.0s
 * - modulator envelope: attack 0.03s, decay 0.25s, sustain 0.3, release 0.8s
 */

const initFMSynthCCs: MidiCC[] = [
  { number: 1, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 7, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 11, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  // FM Synthesis Parameters (even harmonics to complement triangle's odd harmonics)
  { number: 14, value: 19, scramble: true, smooth: false, show: true, min: 0, max: 80 }, // Modulation Index: 3.0 (clean) ✅ SCRAMBLED (FM depth)
  { number: 15, value: 54, scramble: true, smooth: false, show: true, min: 40, max: 80 }, // Harmonicity: 2.0 (octave - even harmonics) ✅ SCRAMBLED
  // Modulator Envelope (softer attack for smoother onset)
  { number: 16, value: 15, scramble: false, smooth: false, show: true, min: 0, max: 100 }, // Mod Env Attack: 0.03s (softer onset)
  { number: 17, value: 45, scramble: false, smooth: false, show: true, min: 5, max: 127 },  // Mod Env Decay: 0.25s (smoother)
  { number: 18, value: 38, scramble: false, smooth: false, show: true, min: 20, max: 127 }, // Mod Env Sustain: 0.3 (less sustain)
  { number: 19, value: 50, scramble: false, smooth: false, show: true, min: 5, max: 127 },  // Mod Env Release: 0.8s (tighter)
  // Carrier Envelope (softer, rounder)
  { number: 70, value: 109, scramble: true, smooth: false, show: true, min: 0, max: 127 },  // Carrier Sustain: 0.86 ✅ SCRAMBLED (volume shape)
  { number: 71, value: 122, scramble: true, smooth: false, show: true, min: 0, max: 127 },  // Filter Resonance ✅ SCRAMBLED
  { number: 72, value: 64, scramble: true, smooth: false, show: true, min: 10, max: 127 }, // Carrier Release: 1.0s ✅ SCRAMBLED (tail length)
  { number: 73, value: 15, scramble: false, smooth: false, show: true, min: 3, max: 80 },   // Carrier Attack: 0.03s (softer onset)
  { number: 74, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },   // Modulator Type: sine (keep stable)
  { number: 75, value: 40, scramble: true, smooth: false, show: true, min: 10, max: 127 }, // Carrier Decay: 0.2s ✅ SCRAMBLED (punch)
  { number: 76, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },   // Carrier Type: sine (keep stable)
  { number: 77, value: 64, scramble: false, smooth: false, show: true, min: 64, max: 64 },  // Detune: 0 cents (no beating)
];

/**
 * Device profile for "Init" FM Synth preset
 */
const initFMSynthProfile: DeviceProfile = {
  id: 'yo-fm-synth-init',
  name: 'Init',
  deviceName: 'YO! FM Synth',
  userId: null, // System profile
  isPublic: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  ccConfig: [
    {
      number: 1,
      enabled: false,
      min: 0,
      max: 127,
      label: 'Modulation',
    },
    {
      number: 7,
      enabled: false,
      min: 80,
      max: 110,
      label: 'Volume',
    },
    {
      number: 11,
      enabled: false,
      min: 80,
      max: 110,
      label: 'Expression',
    },
    {
      number: 14,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Modulation Index',
    },
    {
      number: 15,
      enabled: true,
      min: 0,
      max: 127,
      label: 'Harmonicity',
    },
    {
      number: 16,
      enabled: false, // Disabled - mod env attack kept stable
      min: 0,
      max: 100,
      label: 'Mod Env Attack',
    },
    {
      number: 17,
      enabled: false, // Disabled - mod env decay kept stable
      min: 5,
      max: 127,
      label: 'Mod Env Decay',
    },
    {
      number: 18,
      enabled: false, // Disabled - mod env sustain kept stable
      min: 20,
      max: 127,
      label: 'Mod Env Sustain',
    },
    {
      number: 19,
      enabled: false, // Disabled - mod env release kept stable
      min: 5,
      max: 127,
      label: 'Mod Env Release',
    },
    {
      number: 70,
      enabled: true, // ✅ Carrier Sustain
      min: 0,
      max: 127,
      label: 'Carrier Sustain',
    },
    {
      number: 71,
      enabled: true, // ✅ Filter Resonance
      min: 0,
      max: 127,
      label: 'Filter Resonance',
    },
    {
      number: 72,
      enabled: false, // Disabled - keep preset release time
      min: 10,
      max: 127,
      label: 'Carrier Release',
    },
    {
      number: 73,
      enabled: false, // Disabled - keep preset attack time
      min: 3,
      max: 80,
      label: 'Carrier Attack',
    },
    {
      number: 74,
      enabled: false, // Disabled - modulator type kept stable
      min: 0,
      max: 127,
      label: 'Modulator Type',
    },
    {
      number: 75,
      enabled: true, // ✅ Carrier Decay
      min: 10,
      max: 127,
      label: 'Carrier Decay',
    },
    {
      number: 76,
      enabled: false, // Disabled - carrier type kept stable
      min: 0,
      max: 127,
      label: 'Carrier Type',
    },
    {
      number: 77,
      enabled: false, // Disabled - detune kept stable for phase separation
      min: 60,
      max: 75,
      label: 'Detune',
    },
  ],
};

/**
 * "Init" FM Synth CC Preset
 */
export const INIT_FM_SYNTH_PRESET: CCPreset = {
  id: 'init-fm',
  name: 'Init',
  deviceType: 'FMSynth',
  cc: initFMSynthCCs,
  profiles: [initFMSynthProfile],
  activeProfileId: 'yo-fm-synth-init',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the "Init" FM Synth CC preset
 * Returns a deep copy to avoid mutation
 */
export function getInitFMSynthPreset(): CCPreset {
  return {
    ...INIT_FM_SYNTH_PRESET,
    cc: INIT_FM_SYNTH_PRESET.cc.map(cc => ({ ...cc })),
    profiles: INIT_FM_SYNTH_PRESET.profiles.map(profile => ({
      ...profile,
      ccConfig: profile.ccConfig.map(config => ({ ...config })),
    })),
  };
}
